import { Download, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  descargarAutorizaciones,
  descargarMaestro,
  descargarMiembros,
  descargarReporteObra,
  descargarUsuarios,
  type ExportTipo,
  type FiltrosExport,
  mensajeDeErrorExport,
} from "@/lib/export";
import { MENSAJE_REQUIERE_CONEXION } from "@/lib/offline-policy";
import { useOnlineStatus } from "@/lib/online-status";

/**
 * ExportButtonProps discrimina las variantes de exportación: los listados por obra
 * (#33/#60/#249), que reciben la obra activa en `obraId` y, opcionalmente, `filtros` (los de
 * la URL del listado, #247); las exportaciones de PERSONAS de la obra y el informe general
 * (#249), que no llevan filtros; y la exportación GLOBAL de usuarios (#180), que no cuelga
 * de ninguna obra y por eso no lleva `obraId`. `etiqueta` cambia el texto Y el nombre
 * accesible del botón, para poder distinguir dos exportaciones en la misma pantalla.
 */
type ExportButtonProps = (
  | { tipo: ExportTipo; obraId: number; filtros?: FiltrosExport; etiqueta?: string }
  | {
      tipo: "miembros" | "autorizaciones" | "reporte";
      obraId: number;
      filtros?: undefined;
      etiqueta?: string;
    }
  | { tipo: "usuarios"; obraId?: undefined; filtros?: FiltrosExport; etiqueta?: string }
) & {
  /**
   * motivoDeshabilitado apaga el botón por una razón del LLAMANTE y la explica en el `title`
   * (#307: un rango de fechas que no se cruza con el del listado no se puede pedir, así que la
   * petición no llega a salir). El gate de conectividad es aparte y sigue mandando sobre él.
   */
  motivoDeshabilitado?: string;
};

/**
 * ExportButton descarga la entidad indicada como xlsx. Los listados por obra (tipos
 * onboarded, exportar es leer) cuelgan de la obra activa; `miembros` y `autorizaciones`
 * llevan datos personales y su ruta exige RequireObraAdmin; `reporte` es el informe general
 * de la obra; `usuarios` es un informe GLOBAL ADMIN-only (GET /api/v1/export/usuarios.xlsx,
 * #180) y su botón solo se monta en la página de usuarios (ya ADMIN-only).
 *
 * Exportar SIEMPRE va al servidor —el libro lo construye él—, así que sin red confirmada el
 * botón se deshabilita con «Requiere conexión» (#249 D9). El arranque es fail-closed (#237):
 * mientras el heartbeat no confirma, la conectividad cuenta como caída. Mantiene su propio
 * estado de carga/fallo y muestra el error inline debajo del botón para no depender de un
 * sistema de toasts. Qué DICE ese error lo decide `mensajeDeErrorExport` (#355): un 4xx
 * tipado se cuenta con su `message` («acota los filtros…»), el resto con el texto genérico.
 */
export function ExportButton(props: ExportButtonProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const online = useOnlineStatus();

  async function onExport() {
    setPending(true);
    setError(null);
    try {
      switch (props.tipo) {
        case "usuarios":
          await descargarUsuarios(props.filtros);
          break;
        case "miembros":
          await descargarMiembros(props.obraId);
          break;
        case "autorizaciones":
          await descargarAutorizaciones(props.obraId);
          break;
        case "reporte":
          await descargarReporteObra(props.obraId);
          break;
        default:
          await descargarMaestro(props.tipo, props.obraId, props.filtros);
      }
    } catch (err) {
      setError(mensajeDeErrorExport(err));
    } finally {
      setPending(false);
    }
  }

  const etiqueta = props.etiqueta ?? "Exportar";
  return (
    <div className="flex flex-col items-stretch gap-1">
      <Button
        variant="outline"
        onClick={() => void onExport()}
        disabled={pending || !online || props.motivoDeshabilitado !== undefined}
        title={online ? props.motivoDeshabilitado : MENSAJE_REQUIERE_CONEXION}
        aria-label={props.etiqueta ?? "Exportar a Excel"}
      >
        {pending ? (
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="h-5 w-5" aria-hidden="true" />
        )}
        <span className="hidden sm:inline">{etiqueta}</span>
      </Button>
      {error !== null ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </div>
  );
}
